import { getCurrentUserIdToken, isLoggedIn } from '@/services/auth'
import { composedFetch } from './helpers'
import { getConnectorRootURL } from '@/config'
import { errorIfInvalidResponse } from './errors'
import { configResponseSchema, ApiConfig, captureInvalidSchemaOnSentry } from './schemas'
import { StorageTierCodename, BundlePeriod } from './types/user'

//             _   _                             __ _
//            | | (_)                           / _(_)
//   ___ _ __ | |_ _ _ __ ___    ___ ___  _ __ | |_ _  __ _
//  / _ \ '_ \| __| | '__/ _ \  / __/ _ \| '_ \|  _| |/ _` |
// |  __/ | | | |_| | | |  __/ | (_| (_) | | | | | | | (_| |
//  \___|_| |_|\__|_|_|  \___|  \___\___/|_| |_|_| |_|\__, |
//                                                     __/ |
//                                                    |___/
//

export function getPublicConfigURL(): string {
  return `${getConnectorRootURL()}/config`
}

export function getUserConfigURL(): string {
  return `${getConnectorRootURL()}/config/authorized`
}

/**
 * Get config - public or authorized one, depending on whether the user is logged in.
 * @returns a user config object
 */
export async function apiGetConfig(): Promise<ApiConfig> {
  const loggedIn = isLoggedIn()

  const url = loggedIn ? getUserConfigURL() : getPublicConfigURL()
  const possibleToken = loggedIn ? await getCurrentUserIdToken() : null

  const response = await composedFetch('GET', url, possibleToken)

  await errorIfInvalidResponse(response, 'Failed to get config.')

  const json = await response.json()
  captureInvalidSchemaOnSentry(json, configResponseSchema)

  return json
}

//   _                     _ _                         _
//  | |                   | | |                       | |
//  | |__  _   _ _ __   __| | | ___  ___    ___  _ __ | |_   _
//  | '_ \| | | | '_ \ / _` | |/ _ \/ __|  / _ \| '_ \| | | | |
//  | |_) | |_| | | | | (_| | |  __/\__ \ | (_) | | | | | |_| |
//  |_.__/ \__,_|_| |_|\__,_|_|\___||___/  \___/|_| |_|_|\__, |
//                                                        __/ |
//                                                       |___/

export function getUserBundleURL(): string {
  return `${getConnectorRootURL()}/users/self/bundle`
}

// the arguments we expected from the app data flow
type SaveBundleOptions = {
  codename: StorageTierCodename
  bundlePeriod?: BundlePeriod // potentially optional for old bundles compatibility
  allowTrial?: boolean
}

// the data the API expects
type PatchSelfBundleData = {
  codename: StorageTierCodename
  bundle_period: BundlePeriod
  trial?: boolean
}

/**
 * Save plan selection
 *
 * @param saveBundleOptions including bundle codename, optional bundlePeriod and trial
 * @returns the response json
 */
export async function apiSaveBundle({ codename, bundlePeriod, allowTrial }: SaveBundleOptions) {
  const saveBundleData: PatchSelfBundleData = {
    codename,
    bundle_period: bundlePeriod || 'month',
  }
  if (allowTrial) {
    saveBundleData.trial = allowTrial
  }
  const response = await composedFetch(
    'PATCH',
    getUserBundleURL(),
    await getCurrentUserIdToken(),
    saveBundleData,
  )
  await errorIfInvalidResponse(response)
  return await response.json() // not used, really
}
